import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import StarRatings from "react-star-ratings";
import { useGetProductDetailsQuery } from "../../redux/api/productsApi";
import { Link, Navigate, useParams } from "react-router-dom";
import Loader from "../layout/Loader";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { addViewedProduct } from "../../redux/slices/viewedProductsSlice";
import PreviouslyViewed from "./PreviouslyViewed";
import { setCartItem } from "../../redux/slices/cartSlice";
import { openCart } from "../../redux/slices/uiSlice";
import NewReview from "../reviews/NewReview";
import { categoryLabels } from "../../constants/constants";
import { Helmet } from "react-helmet-async";
import {
  ChevronLeft, ChevronRight, Plus,
  Leaf, Heart, Recycle, Sun, Package,
  Globe, Shield, Star, Gift, Pencil,
  Frame, Shirt,
} from "lucide-react";
import DOMPurify from "dompurify";

// ─── Typography system ────────────────────────────────────────────────────────
//   Headings → font-playfair font-normal
//   Body/UI  → font-sourcesans
//   Labels   → font-mono text-[0.6rem] tracking-[0.3em] uppercase text-stone-400

// ─── Feature badge system ─────────────────────────────────────────────────────
//
// Two sources — no keyword scanning, fully explicit:
//
//   1. CATEGORY_BADGES: auto-applied based on product.category
//   2. FEATURE_MAP: applied when product.features[] contains a matching key
//      e.g. product.features = ["handmade", "ukraine", "eco"]
//
// To add a new badge: add one line to FEATURE_MAP and ensure your API
// populates product.features accordingly.

const CATEGORY_BADGES = {
  Cards:       [{ Icon: Gift,   label: "Handmade card"  }],
  Photography: [{ Icon: Frame,  label: "Fine art print" }],
  Art:         [{ Icon: Pencil, label: "Original art"   }],
  Apparel:     [{ Icon: Shirt,  label: "Wearable"       }],
};

const FEATURE_MAP = {
  ukraine:      { Icon: Heart,   label: "Supports Ukraine"   },
  recycled:     { Icon: Recycle, label: "Recycled materials" },
  eco:          { Icon: Leaf,    label: "Eco-friendly"       },
  handmade:     { Icon: Gift,    label: "Handmade"           },
  uv:           { Icon: Sun,     label: "UV-resistant"       },
  worldwide:    { Icon: Globe,   label: "Ships worldwide"    },
  "string art": { Icon: Star,    label: "String art"         },
  gift:         { Icon: Package, label: "Gift-ready"         },
  guaranteed:   { Icon: Shield,  label: "Quality guaranteed" },
};

function getFeatureBadges(product) {
  const badges = [];
  const seen   = new Set();

  (CATEGORY_BADGES[product.category] || []).forEach((b) => {
    if (!seen.has(b.label)) { badges.push(b); seen.add(b.label); }
  });

  (product.features || []).forEach((key) => {
    const b = FEATURE_MAP[key?.toLowerCase()];
    if (b && !seen.has(b.label)) { badges.push(b); seen.add(b.label); }
  });

  return badges;
}

// ─── Cloudinary URL helper ────────────────────────────────────────────────────

const cloudUrl = (url, w = 900) =>
  url?.replace("/upload/", `/upload/f_auto,q_auto,w_${w}/`);

// ─── Gallery ──────────────────────────────────────────────────────────────────

const Gallery = ({ images, productName }) => {
  const [active, setActive] = useState(0);
  const [loaded, setLoaded] = useState({});
  const imgRefs    = useRef([]);
  const touchStart = useRef(null);

  useEffect(() => {
    setLoaded((prev) => {
      const next = { ...prev };
      imgRefs.current.forEach((el, i) => {
        if (el?.complete && el.naturalWidth > 0) next[i] = true;
      });
      return next;
    });
  }, [images]);

  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd   = (e) => {
    if (touchStart.current === null) return;
    const delta = touchStart.current - e.changedTouches[0].clientX;
    touchStart.current = null;
    if (Math.abs(delta) < 40) return;
    setActive((a) => delta > 0
      ? Math.min(a + 1, images.length - 1)
      : Math.max(a - 1, 0));
  };

  return (
    <div className="flex flex-col gap-3 md:sticky md:top-28 md:self-start">

      {/* Main image */}
      <div
        className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden bg-[#f5f3ea]"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {!loaded[active] && (
          <div className="absolute inset-0 animate-pulse bg-[#ece9e0] rounded-2xl" />
        )}

        {images.map((img, i) => (
          <img
            key={i}
            ref={(el) => (imgRefs.current[i] = el)}
            src={cloudUrl(img?.url)}
            alt={i === active ? `${productName} — view ${i + 1}` : ""}
            aria-hidden={i !== active}
            className="absolute inset-0 w-full h-full object-contain"
            style={{
              opacity:       i === active ? 1 : 0,
              transform:     i === active ? "scale(1)" : "scale(1.03)",
              transition:    "opacity 300ms ease, transform 300ms ease",
              pointerEvents: i === active ? "auto" : "none",
            }}
            loading={i === 0 ? "eager" : "lazy"}
            fetchpriority={i === 0 ? "high" : "auto"}
            decoding={i === 0 ? "sync" : "async"}
            onLoad={() => setLoaded((p) => ({ ...p, [i]: true }))}
          />
        ))}

        {images.length > 1 && (
          <>
            <button
              onClick={() => setActive((a) => Math.max(a - 1, 0))}
              disabled={active === 0}
              aria-label="Previous image"
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 backdrop-blur-sm shadow-sm hover:bg-white transition disabled:opacity-0"
            >
              <ChevronLeft className="w-4 h-4 text-stone-700" />
            </button>
            <button
              onClick={() => setActive((a) => Math.min(a + 1, images.length - 1))}
              disabled={active === images.length - 1}
              aria-label="Next image"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 backdrop-blur-sm shadow-sm hover:bg-white transition disabled:opacity-0"
            >
              <ChevronRight className="w-4 h-4 text-stone-700" />
            </button>

            {/* Counter pill — desktop */}
            <div className="hidden md:block absolute bottom-3 right-3 px-2.5 py-1 rounded-full bg-black/25 backdrop-blur-sm">
              <span className="font-mono text-[0.6rem] text-white/90 tracking-wider">
                {active + 1} / {images.length}
              </span>
            </div>

            {/* Dot nav — mobile */}
            <div className="md:hidden absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  aria-label={`Image ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-200
                    ${i === active ? "bg-white w-4" : "bg-white/50 w-1.5"}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Thumbnail strip — desktop only */}
      {images.length > 1 && (
        <div className="hidden md:flex gap-2 overflow-x-auto pb-1 snap-x">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              aria-label={`View image ${i + 1}`}
              aria-pressed={i === active}
              className={`shrink-0 w-16 h-20 rounded-xl overflow-hidden border-2 transition-all snap-start bg-[#f5f3ea]
                ${i === active
                  ? "border-[#BC3F4A]"
                  : "border-transparent opacity-55 hover:opacity-85 hover:border-stone-200"}`}
            >
              <img
                src={cloudUrl(img?.url, 160)}
                alt=""
                aria-hidden="true"
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Stars wrapper ────────────────────────────────────────────────────────────

const Stars = ({ rating, size = 14, name }) => (
  <StarRatings
    rating={rating || 0}
    starEmptyColor="#ddd8ce"
    starRatedColor="#C19A6B"
    numberOfStars={5}
    starDimension={`${size}px`}
    starSpacing="1px"
    name={name}
  />
);

// ─── Feature badges ───────────────────────────────────────────────────────────

const FeatureBadges = ({ product }) => {
  const badges = getFeatureBadges(product);
  if (!badges.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-5">
      {badges.map(({ Icon, label }) => (
        <span
          key={label}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-200 bg-white/50"
        >
          <Icon className="w-3.5 h-3.5 text-[#3fbcb1] shrink-0" strokeWidth={1.75} />
          <span className="font-mono text-[0.58rem] tracking-[0.12em] uppercase text-stone-500">
            {label}
          </span>
        </span>
      ))}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const ProductDetailPage = () => {
  const [quantity,  setQuantity]  = useState(1);
  const [activeTab, setActiveTab] = useState(0);

  const dispatch  = useDispatch();
  const cartItems = useSelector((s) => s.cart.cartItems);
  const { isAuthenticated } = useSelector((s) => s.auth);

  const { id } = useParams();
  const { data, isLoading, isError, error } = useGetProductDetailsQuery(id);
  const reviewsRef = useRef(null);

  useEffect(() => {
    if (isError && error?.status !== 404)
      toast.error(error?.data?.message || error?.message || "Failed to load product");
    else if (data?.product)
      dispatch(addViewedProduct(data.product));
  }, [isError, error, data?.product, dispatch]);

  if (isLoading) return <Loader />;
  const product = data?.product;
  if (!product) return <Navigate to="/404" replace />;

  const { name, price, description, ratings, images } = product;
  const cleanDesc = DOMPurify.sanitize(description || "", { ALLOWED_TAGS: [] });

  const sections = [
    { key: "details",     content: product.details     || "" },
    { key: "ecoFeatures", content: product.ecoFeatures || "" },
    { key: "usageTips",   content: product.usageTips   || "" },
  ].filter((s) => s.content);

  const labels = categoryLabels[product.category] || {
    details:     "Details",
    ecoFeatures: "Eco Features",
    usageTips:   "Usage Tips",
  };

  const addItemToCart = () => {
    const existing = cartItems.find((i) => i.product === product._id);
    if (existing && existing.quantity >= product.stock) {
      toast.error("Maximum items already added"); return;
    }
    if (existing) {
      toast.error("Already in your bag"); return;
    }
    dispatch(setCartItem({
      product:  product._id,
      name:     product.name,
      price:    product.price,
      image:    product.images[0]?.url,
      stock:    product.stock,
      quantity,
    }));
    dispatch(openCart());
    toast.success("Added to bag");
  };

  const showUkraineBanner =
    product.showUkraineBanner || product.features?.includes("ukraine");

  return (
    <>
      <Helmet>
        <title>{name} — Sanguine Accent</title>
        <meta name="description" content={cleanDesc.slice(0, 155) || "Handcrafted string art cards and fine art photography."} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://sanguineaccent.com/products/${product._id}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name,
          image: images[0]?.url,
          description: cleanDesc.slice(0, 300),
          offers: {
            "@type": "Offer",
            priceCurrency: "USD",
            price,
            availability: product.stock > 0
              ? "https://schema.org/InStock"
              : "https://schema.org/OutOfStock",
          },
        })}</script>
      </Helmet>

      <div className="bg-[#f5f3ea] min-h-screen">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 pt-24 md:pt-48 pb-24">

          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-8 flex items-center gap-2 flex-wrap">
            <Link to="/" className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-stone-400 hover:text-stone-700 transition-colors">
              Home
            </Link>
            <span className="font-mono text-[0.6rem] text-stone-300">/</span>
            <Link to="/supportUkraine" className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-stone-400 hover:text-stone-700 transition-colors">
              {product.category || "Shop"}
            </Link>
            <span className="font-mono text-[0.6rem] text-stone-300">/</span>
            <span className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-stone-600 truncate max-w-[160px]">
              {name}
            </span>
          </nav>

          {/* Two-column grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-start">

            <Gallery images={images} productName={name} />

            {/* Details */}
            <div className="flex flex-col">

              <p className="font-mono text-[0.6rem] tracking-[0.3em] uppercase text-stone-400 mb-2">
                {product.category}
              </p>

              <h1
                className="font-playfair font-normal text-stone-900 leading-tight"
                style={{ fontSize: "clamp(1.6rem, 3.5vw, 2.25rem)", textWrap: "balance" }}
              >
                {name}
              </h1>

              {/* Price + stars */}
              <div className="mt-3 flex items-center gap-4 flex-wrap">
                <span
                  className="font-playfair font-normal text-stone-800"
                  style={{ fontSize: "clamp(1.2rem, 2.5vw, 1.5rem)" }}
                >
                  ${price.toFixed(2)}
                </span>
                <button
                  onClick={() => reviewsRef.current?.scrollIntoView({ behavior: "smooth" })}
                  aria-label="Scroll to reviews"
                  className="flex items-center gap-1.5 group"
                >
                  <Stars rating={ratings} name="pdp-rating" />
                  <span className="font-mono text-[0.6rem] tracking-[0.12em] uppercase text-stone-400 group-hover:text-stone-600 transition-colors">
                    ({product.numOfReviews})
                  </span>
                </button>
              </div>

              {/* Stock pill */}
              <div className="mt-4">
                <span className={`inline-flex items-center gap-1.5 font-mono text-[0.6rem] tracking-[0.15em] uppercase px-3 py-1 rounded-full border
                  ${product.stock > 0
                    ? "bg-teal-50 text-[#3fbcb1] border-[#3fbcb1]/25"
                    : "bg-red-50 text-red-400 border-red-200"}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${product.stock > 0 ? "bg-[#3fbcb1]" : "bg-red-400"}`} />
                  {product.stock > 0 ? "In stock" : "Out of stock"}
                </span>
              </div>

              <FeatureBadges product={product} />

              <div className="my-5 h-px bg-stone-200" />

              {/* Qty + CTA — immediately after price/stock so it's always visible */}
              <div className="flex items-stretch gap-3">
                <div className="flex items-center rounded-xl border border-stone-300 bg-white/50 overflow-hidden">
                  <button
                    type="button"
                    aria-label="Decrease quantity"
                    onClick={() => setQuantity((q) => Math.max(q - 1, 1))}
                    disabled={quantity <= 1}
                    className="w-11 h-11 flex items-center justify-center text-stone-600 text-xl font-light hover:bg-stone-100 disabled:opacity-25 transition"
                  >−</button>
                  <span className="w-9 text-center font-sourcesans text-sm text-stone-800 select-none tabular-nums">
                    {quantity}
                  </span>
                  <button
                    type="button"
                    aria-label="Increase quantity"
                    onClick={() => setQuantity((q) => Math.min(q + 1, product.stock))}
                    disabled={quantity >= product.stock}
                    className="w-11 h-11 flex items-center justify-center text-stone-600 text-xl font-light hover:bg-stone-100 disabled:opacity-25 transition"
                  >+</button>
                </div>

                <button
                  onClick={addItemToCart}
                  disabled={product.stock <= 0}
                  className="flex-1 h-11 rounded-xl font-sourcesans text-sm font-medium
                    bg-[#BC3F4A] text-white hover:bg-[#a8333d] active:scale-[0.98]
                    transition-all duration-200
                    disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-stone-300 disabled:text-stone-500"
                >
                  {product.stock > 0 ? "Add to Bag" : "Out of Stock"}
                </button>
              </div>

              {/* Ukraine banner — right under CTA for purchase context */}
              {showUkraineBanner && (
                <div className="mt-3 flex items-start gap-2.5 px-4 py-3 rounded-xl border border-[#BC3F4A]/20 bg-[#BC3F4A]/5">
                  <Heart className="w-3.5 h-3.5 text-[#BC3F4A] shrink-0 mt-0.5" strokeWidth={2} />
                  <p className="font-sourcesans text-xs text-stone-600 leading-relaxed">
                    <span className="font-semibold text-stone-800">70% of this purchase</span>{" "}
                    goes directly to people serving in Ukraine.
                  </p>
                </div>
              )}

              <div className="my-6 h-px bg-stone-200" />

              {/* Description — below CTA, for those who want to read more */}
              <p
                className="font-sourcesans text-sm text-stone-600 leading-relaxed whitespace-pre-line max-w-prose"
                style={{ textWrap: "pretty" }}
              >
                {description}
              </p>

              <p className="mt-3 font-mono text-[0.58rem] tracking-[0.15em] uppercase text-stone-400">
                Sold by{" "}
                <span className="normal-case tracking-normal font-sourcesans text-xs text-stone-600">
                  {product.seller}
                </span>
              </p>

              {/* Tabs / Accordion */}
              {sections.length > 0 && (
                <div className="mt-8 border-t border-stone-200 pt-6">

                  {/* Desktop tabs */}
                  <div className="hidden md:block">
                    <div role="tablist" className="flex gap-6 border-b border-stone-200">
                      {sections.map((s, i) => (
                        <button
                          key={s.key}
                          role="tab"
                          id={`tab-${i}`}
                          aria-selected={activeTab === i}
                          aria-controls={`panel-${i}`}
                          onClick={() => setActiveTab(i)}
                          className={`pb-3 font-mono text-[0.6rem] tracking-[0.2em] uppercase border-b-2 -mb-px transition-colors
                            ${activeTab === i
                              ? "border-[#BC3F4A] text-[#BC3F4A]"
                              : "border-transparent text-stone-400 hover:text-stone-700"}`}
                        >
                          {labels[s.key]}
                        </button>
                      ))}
                    </div>

                    <AnimatePresence mode="wait">
                      <motion.p
                        key={activeTab}
                        role="tabpanel"
                        id={`panel-${activeTab}`}
                        aria-labelledby={`tab-${activeTab}`}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.18 }}
                        className="mt-4 font-sourcesans text-sm text-stone-600 leading-relaxed"
                        style={{ whiteSpace: "pre-wrap", textWrap: "pretty" }}
                      >
                        {sections[activeTab]?.content}
                      </motion.p>
                    </AnimatePresence>
                  </div>

                  {/* Mobile accordion */}
                  <div className="md:hidden divide-y divide-stone-200">
                    {sections.map((s, i) => {
                      const isOpen = activeTab === i;
                      return (
                        <div key={s.key}>
                          <button
                            onClick={() => setActiveTab(isOpen ? -1 : i)}
                            aria-expanded={isOpen}
                            aria-controls={`panel-mob-${i}`}
                            className="w-full py-4 flex items-center justify-between text-left"
                          >
                            <span className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-stone-600">
                              {labels[s.key]}
                            </span>
                            <motion.span
                              animate={{ rotate: isOpen ? 45 : 0 }}
                              transition={{ duration: 0.2 }}
                              className="shrink-0 block"
                            >
                              <Plus className="w-3.5 h-3.5 text-stone-400" />
                            </motion.span>
                          </button>

                          <AnimatePresence initial={false}>
                            {isOpen && (
                              <motion.div
                                id={`panel-mob-${i}`}
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.22 }}
                                className="overflow-hidden"
                              >
                                <p
                                  className="pb-4 font-sourcesans text-sm text-stone-600 leading-relaxed"
                                  style={{ whiteSpace: "pre-wrap", textWrap: "pretty" }}
                                >
                                  {s.content}
                                </p>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Reviews */}
              <div ref={reviewsRef} className="mt-10 border-t border-stone-200 pt-6">
                {isAuthenticated ? (
                  <NewReview productId={product._id} reviews={product.reviews} />
                ) : (
                  <p className="font-sourcesans text-sm text-stone-500 text-center border border-stone-200 rounded-xl p-4 bg-white/40">
                    <Link to="/login" className="text-[#BC3F4A] font-medium hover:underline">
                      Sign in
                    </Link>{" "}
                    to leave a review.
                  </p>
                )}

                <div className="flex items-baseline gap-3 mt-8 mb-4">
                  <p className="font-mono text-[0.6rem] tracking-[0.3em] uppercase text-stone-400">
                    Reviews
                  </p>
                  <span className="font-playfair font-normal text-stone-800 text-md">
                    {product.numOfReviews === 0 ? "(0)" : `(${product.numOfReviews})`}
                  </span>
                </div>

                <div className="space-y-3">
                  {(product.reviews || []).map((rev, idx) => (
                    <div
                      key={rev._id || idx}
                      className="p-4 rounded-xl border border-stone-200 bg-white/50"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-sourcesans text-sm font-medium text-stone-800">
                          {rev.user?.name || "Anonymous"}
                        </p>
                        <Stars rating={rev.rating} size={12} name={`rev-${rev._id || idx}`} />
                      </div>
                      <p
                        className="font-sourcesans text-sm text-stone-600 leading-relaxed"
                        style={{ textWrap: "pretty" }}
                      >
                        {rev.comment}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Previously viewed */}
          <div className=" border-t border-stone-200 pt-12">
            <PreviouslyViewed />
          </div>

        </div>
      </div>
    </>
  );
};

export default ProductDetailPage;